import React, { useState, useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  transactionsApi,
  type GetAllTransactionsFilters,
  bankAccountsApi,
  costCentersApi,
  subcategoriesApi,
} from "../../../services";
import type { ImportResult } from "../../../types";
import type { TransactionAttributes } from "../../../types";
import type { BankAccountAttributes } from "../../../types";
import type { CostCenterAttributes } from "../../../types";
import type { SubcategoryAttributes } from "../../../types";
import {
  ActionButton,
  ConfirmationModal,
  ErrorState,
  ImportResultCard,
  ExcelImportButton,
  IconsLarge,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { CreateTransactionModal } from "./CreateTransactionModal";
import { CreateInputTransactionModal } from "./CreateInputTransactionModal";
import { CreateTransferTransactionModal } from "./CreateTransferTransactionModal";
import { TransactionsListView } from "./TransactionsListView";
import { getBrazilCalendarMonthRange } from "../../../utils/dateUtils";

/**
 * Place your workbook at:
 * `public/templates/transactions-import-template.xlsx`
 * (served as `/templates/transactions-import-template.xlsx` when the app base is `/`).
 */
const TRANSACTIONS_IMPORT_TEMPLATE_FILE = "transactions-import-template.xlsx";

function getTransactionsImportTemplateHref(): string {
  const prefix = (process.env.PUBLIC_URL ?? "").replace(/\/?$/, "");
  return `${prefix}/templates/${TRANSACTIONS_IMPORT_TEMPLATE_FILE}`;
}

export const Transaction: React.FC = () => {
  const { company, refreshCompaniesFromApi } = useCompany();
  const companyId = company?.id ?? 0;
  const [transactions, setTransactions] = useState<TransactionAttributes[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryAttributes[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccountAttributes[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<TransactionAttributes | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<number | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<(string | number)[]>([]);
  const [bulkEditIds, setBulkEditIds] = useState<number[] | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [createFormVariant, setCreateFormVariant] = useState<"input" | "output">("output");
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [modalSubcategories, setModalSubcategories] = useState<SubcategoryAttributes[]>([]);
  const [modalSubcategoriesLoading, setModalSubcategoriesLoading] = useState(false);
  const [editPrefetchLoading, setEditPrefetchLoading] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<"competence" | "due" | "payment">("competence");
  const [dateRangeFrom, setDateRangeFrom] = useState(() => getBrazilCalendarMonthRange().from);
  const [dateRangeTo, setDateRangeTo] = useState(() => getBrazilCalendarMonthRange().to);

  const buildDateFilters = (override?: {
    dateFilterType: "competence" | "due" | "payment";
    dateRangeFrom: string;
    dateRangeTo: string;
  }): GetAllTransactionsFilters | undefined => {
    const type = override?.dateFilterType ?? dateFilterType;
    const from = (override?.dateRangeFrom ?? dateRangeFrom).trim();
    const to = (override?.dateRangeTo ?? dateRangeTo).trim();
    if (!from && !to) return undefined;
    if (type === "competence") return { competenceDateFrom: from || undefined, competenceDateTo: to || undefined };
    if (type === "due") return { dueDateFrom: from || undefined, dueDateTo: to || undefined };
    return { paymentDateFrom: from || undefined, paymentDateTo: to || undefined };
  };

  const fetchTransactions = async (dateFilterOverride?: {
    dateFilterType: "competence" | "due" | "payment";
    dateRangeFrom: string;
    dateRangeTo: string;
  }) => {
    if (companyId === 0) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const filters = buildDateFilters(dateFilterOverride);
    try {
      const [txRes, subRes, accRes, costRes] = await Promise.all([
        transactionsApi.getAll(companyId, filters),
        subcategoriesApi.getAll(companyId),
        bankAccountsApi.getAll(companyId),
        costCentersApi.getAll(companyId),
      ]);
      if (txRes.success && txRes.data) setTransactions(txRes.data);
      else setError(txRes.error ?? "Falha ao carregar transações");
      if (subRes.success && subRes.data) setSubcategories(subRes.data);
      if (accRes.success && accRes.data) setBankAccounts(accRes.data);
      if (costRes.success && costRes.data) setCostCenters(costRes.data);
    } catch {
      setError("Falha ao carregar transações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshCompaniesFromApi();
  }, [refreshCompaniesFromApi]);

  useEffect(() => {
    fetchTransactions();
  }, [companyId]);

  useEffect(() => {
    if (!showCreateForm || companyId === 0) {
      setModalSubcategories([]);
      return;
    }
    let cancelled = false;
    setModalSubcategoriesLoading(true);
    subcategoriesApi.getAll(companyId, undefined, createFormVariant).then((res) => {
      if (!cancelled && res.success && res.data) setModalSubcategories(res.data);
      if (!cancelled) setModalSubcategoriesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [showCreateForm, companyId, createFormVariant]);

  const handleEdit = async (transactionId: number) => {
    setShowTransferModal(false);
    setEditPrefetchLoading(true);
    setError(null);
    try {
      const res = await transactionsApi.getById(transactionId);
      if (!res.success || !res.data) {
        setError(res.error ?? "Não foi possível carregar o lançamento para edição.");
        return;
      }
      if (res.data.companyId !== companyId) {
        setError("Este lançamento não pertence à empresa selecionada.");
        return;
      }
      setEditingTransaction(res.data);
      setCreateFormVariant(res.data.type === "input" ? "input" : "output");
      setShowCreateForm(true);
    } catch {
      setError("Não foi possível carregar o lançamento para edição.");
    } finally {
      setEditPrefetchLoading(false);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setShowTransferModal(false);
    setEditingTransaction(null);
    setBulkEditIds(null);
  };

  const handleSaved = () => {
    fetchTransactions();
    setShowCreateForm(false);
    setShowTransferModal(false);
    setEditingTransaction(null);
    setBulkEditIds(null);
    setSelectedTransactionIds([]);
  };

  const handleOpenBulkEdit = () => {
    const ids = selectedTransactionIds.map((id) => (typeof id === "string" ? Number(id) : id)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setShowTransferModal(false);
    setBulkEditIds(ids);
    setEditingTransaction(null);
    setCreateFormVariant("output");
    setShowCreateForm(true);
  };

  const handleDelete = (id: number) => {
    setTransactionToDelete(id);
    setShowDeleteModal(true);
  };

  const handleOpenBulkDelete = () => {
    if (selectedTransactionIds.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    const ids = selectedTransactionIds
      .map((id) => (typeof id === "string" ? Number(id) : id))
      .filter((n) => !Number.isNaN(n));
    if (ids.length === 0) {
      setShowBulkDeleteModal(false);
      return;
    }
    setBulkDeleteLoading(true);
    setError(null);
    try {
      let failed = 0;
      for (const id of ids) {
        const response = await transactionsApi.delete(id);
        if (!response.success) failed += 1;
      }
      await fetchTransactions(undefined);
      setShowBulkDeleteModal(false);
      setSelectedTransactionIds([]);
      if (failed > 0) {
        setError(
          failed === ids.length
            ? "Não foi possível excluir os lançamentos selecionados."
            : `${failed} de ${ids.length} lançamento(s) não puderam ser excluídos.`
        );
      }
    } catch {
      setError("Falha ao excluir lançamentos.");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;
    setDeleteLoading(true);
    setError(null);
    try {
      const response = await transactionsApi.delete(transactionToDelete);
      if (response.success) {
        await fetchTransactions(undefined);
        setShowDeleteModal(false);
        setTransactionToDelete(null);
      } else {
        setError(response.error ?? "Falha ao excluir transação");
      }
    } catch {
      setError("Falha ao excluir transação");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTransactionToDelete(null);
  };

  const handleImportFile = async (file: File) => {
    if (companyId === 0) return;
    setImportLoading(true);
    setError(null);
    setImportResult(null);
    try {
      const response = await transactionsApi.importFromExcel(companyId, file);
      if (response.success && response.data) {
        setImportResult(response.data);
        if ((response.data.entities?.transactions?.created ?? 0) > 0) {
          await fetchTransactions();
        }
      } else {
        setError(response.error ?? "Falha ao importar planilha.");
      }
    } catch {
      setError("Falha ao importar planilha.");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<IconsLarge.Transactions />}
        title="Lançamentos"
        subtitle="Gerencie os lançamentos financeiros"
        showViewToggle={false}
        ctaStrip={
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                Ações rápidas
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400 sm:text-right max-w-xl">
                Cadastre lançamentos, transferências ou importe planilhas usando o modelo oficial.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ActionButton
                type="button"
                variant="outline"
                size="sm"
                icon="+"
                disabled={loading}
                className="flex-shrink-0"
                onClick={() => {
                  setEditingTransaction(null);
                  setBulkEditIds(null);
                  setShowTransferModal(false);
                  setCreateFormVariant("input");
                  setShowCreateForm(true);
                }}
              >
                Nova entrada
              </ActionButton>
              <ActionButton
                type="button"
                variant="outline"
                size="sm"
                icon="—"
                disabled={loading}
                className="flex-shrink-0"
                onClick={() => {
                  setEditingTransaction(null);
                  setBulkEditIds(null);
                  setShowTransferModal(false);
                  setCreateFormVariant("output");
                  setShowCreateForm(true);
                }}
              >
                Nova saída
              </ActionButton>
              <ActionButton
                type="button"
                variant="outline"
                size="sm"
                icon="⇄"
                disabled={loading}
                className="flex-shrink-0"
                onClick={() => {
                  setEditingTransaction(null);
                  setBulkEditIds(null);
                  setShowCreateForm(false);
                  setShowTransferModal(true);
                }}
              >
                Nova transferência
              </ActionButton>
              <ActionButton
                href={getTransactionsImportTemplateHref()}
                download={TRANSACTIONS_IMPORT_TEMPLATE_FILE}
                variant="outline"
                disabled={loading}
                size="sm"
                icon="⬇"
                className="flex-shrink-0"
              >
                Baixar modelo Excel
              </ActionButton>
              <ExcelImportButton
                onFileSelect={handleImportFile}
                disabled={loading || companyId === 0}
                loading={importLoading}
              />
            </div>
          </div>
        }
      />

      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => fetchTransactions()}
        />
      )}

      {importResult && (
        <ImportResultCard
          result={importResult}
          onClose={() => setImportResult(null)}
        />
      )}

      <CreateInputTransactionModal
        isOpen={showCreateForm && createFormVariant === "input"}
        onClose={handleCancel}
        onSuccess={handleSaved}
        companyId={companyId}
        subcategories={createFormVariant === "input" ? modalSubcategories : []}
        subcategoriesLoading={createFormVariant === "input" && modalSubcategoriesLoading}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        editingTransaction={createFormVariant === "input" ? editingTransaction : null}
        transactionIds={undefined}
      />
      <CreateTransferTransactionModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSuccess={handleSaved}
        companyId={companyId}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
      />

      <CreateTransactionModal
        isOpen={showCreateForm && createFormVariant === "output"}
        onClose={handleCancel}
        onSuccess={handleSaved}
        companyId={companyId}
        subcategories={createFormVariant === "output" ? modalSubcategories : []}
        subcategoriesLoading={createFormVariant === "output" && modalSubcategoriesLoading}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        editingTransaction={createFormVariant === "output" ? editingTransaction : null}
        transactionIds={bulkEditIds ?? undefined}
      />

      {/*
        Lista permanece montada durante refetch (loading), senão o estado interno dos filtros
        do modal e outros resets ao desmontar — ex.: ao clicar "Aplicar filtros".
      */}
      <TransactionsListView
        transactions={transactions}
        subcategories={subcategories}
        bankAccounts={bankAccounts}
        costCenters={costCenters}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkEdit={handleOpenBulkEdit}
        onBulkDelete={handleOpenBulkDelete}
        loading={loading || editPrefetchLoading}
        selectedTransactionIds={selectedTransactionIds}
        onSelectionChange={setSelectedTransactionIds}
        dateFilterType={dateFilterType}
        dateRangeFrom={dateRangeFrom}
        dateRangeTo={dateRangeTo}
        onDateFilterChange={(filter) => {
          setDateFilterType(filter.dateFilterType);
          setDateRangeFrom(filter.dateRangeFrom);
          setDateRangeTo(filter.dateRangeTo);
        }}
        onDateFilterApply={(filter) => fetchTransactions(filter)}
        onEmptyCreateOutput={() => {
          setCreateFormVariant("output");
          setShowCreateForm(true);
        }}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir transação"
        message="Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />

      <ConfirmationModal
        isOpen={showBulkDeleteModal}
        title="Excluir lançamentos"
        message={`Tem certeza que deseja excluir ${selectedTransactionIds.length} lançamento(s)? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
        loading={bulkDeleteLoading}
      />
    </div>
  );
};

export default Transaction;
